import React, { useEffect, useState, useRef, useCallback } from 'react';
import { MESSAGE_TYPE, useFloatMessage } from '../../../Providers/FloatMessage';
import { SecondaryBase } from '../../Commons/SecondaryBase';
import { Confirm } from '../../../Commons/Modals/Confirm';
import {
    GetAdminParticipants,
    GetAdminStats,
    GetAdminConfig,
    UpdateAdminConfig,
    SetScreenVisibility,
    UndisqualifyParticipant,
    ResetDynamic,
    GetExportExcelUrl,
    formatMexicoDate,
    SendQROptimoOpticas
} from '../../../../utilities/BL/OptimoOpticas';

import { OptimoOpticasConfigModal } from './OptimoOpticasConfigModal';
import { OptimoOpticasCheckinModal } from './OptimoOpticasCheckinModal';
import { OptimoOpticasDisqualifyModal } from './OptimoOpticasDisqualifyModal';
import { OptimoOpticasDangerModal } from './OptimoOpticasDangerModal';

/**
 * Panel Administrativo Central (Tile ID 49) para la dinámica 'Rasca y Gana - Optimo Ópticas'.
 * Proporciona telemetría de embudo en tiempo real, gestión de cupones, control de proyección (/screen),
 * padrón paginado de participantes, módulo de canje staff y auditoría de descalificación/restitución.
 *
 * @component
 * @param {Object} props
 * @param {Function} props.getOutOfTheDynamic - Callback para regresar al Dashboard principal de administración.
 */
export function OptimoOpticasBoard({ getOutOfTheDynamic }) {
    const { showMessage } = useFloatMessage();

    // ==========================================
    // ESTADOS DE DATOS
    // ==========================================
    const [participants, setParticipants] = useState([]);
    const [config, setConfig] = useState({
        matchTitle: 'Atlético de San Luis vs Rival',
        campaignTitle: 'Rasca y Gana Optimo Ópticas',
        winnerMode: 'LIMITED',
        maxWinners: 50,
        couponPrefix: 'OPTI',
        discountDescription: 'Cupón válido por descuento especial en armazones y micas seleccionadas.',
        storageExpirationDays: 30,
        isDynamicActive: true,
        isScreenCoverVisible: true,
        showPassUserData: false,
        showPassQr: true,
        showPassLocationNote: true,
        additionalInstructions: 'Presenta este cupón digital en cualquier sucursal de Optimo Ópticas.'
    });

    const [stats, setStats] = useState({
        qrScan: 0,
        pageView: 0,
        formStart: 0,
        formComplete: 0,
        gameStart: 0,
        couponsDiscovered: 0,
        screenshotsSaved: 0,
        couponsRecovered: 0,
        couponsRedeemed: 0
    });

    const [isLoading, setIsLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [isScreenCoverVisible, setIsScreenCoverVisible] = useState(true);
    const [isProjecting, setIsProjecting] = useState(false);

    // ==========================================
    // ESTADOS DE MODALES Y SECCIONES
    // ==========================================
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const [isCheckinModalOpen, setIsCheckinModalOpen] = useState(false);
    const [isDangerModalOpen, setIsDangerModalOpen] = useState(false);
    const [isDangerZoneOpen, setIsDangerZoneOpen] = useState(false);
    const [isDynamicActiveCardOpen, setIsDynamicActiveCardOpen] = useState(false);
    const [disqualifyTarget, setDisqualifyTarget] = useState(null);

    // Modal de Confirmación Genérico
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({
        message: '',
        action: null,
        textYes: 'Sí, confirmar',
        textNo: 'Cancelar'
    });

    // ==========================================
    // BÚSQUEDA Y FILTROS
    // ==========================================
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'redeemed' | 'pending' | 'disqualified'

    // ==========================================
    // PAGINACIÓN
    // ==========================================
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    // ==========================================
    // WEBSOCKET PERSISTENTE
    // ==========================================
    const webSocketRef = useRef(null);

    /**
     * Carga inicial y refresco manual de participantes, estadísticas y configuración.
     */
    const loadInitialData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [partRes, statsRes, configRes] = await Promise.all([
                GetAdminParticipants(),
                GetAdminStats(),
                GetAdminConfig()
            ]);

            if (partRes && partRes.success && partRes.data) {
                const list = partRes.data.items
                    || partRes.data.participants
                    || (Array.isArray(partRes.data) ? partRes.data : []);
                setParticipants(list);
            }

            if (statsRes && statsRes.success && statsRes.data) {
                const s = statsRes.data;
                setStats({
                    qrScan: s.qrScan ?? s.qrScans ?? 0,
                    pageView: s.pageView ?? s.pageViews ?? 0,
                    formStart: s.formStart ?? s.formStarts ?? 0,
                    formComplete: s.formComplete ?? s.formCompletes ?? 0,
                    gameStart: s.gameStart ?? s.scratchStarts ?? s.scratchesStarted ?? 0,
                    couponsDiscovered: s.couponsDiscovered ?? s.couponsAwarded ?? s.passesIssued ?? 0,
                    screenshotsSaved: s.screenshotsSaved ?? s.screenshotsCount ?? 0,
                    couponsRecovered: s.couponsRecovered ?? s.passesRecovered ?? 0,
                    couponsRedeemed: s.couponsRedeemed ?? s.passesAttended ?? s.redeemedCount ?? 0
                });
            }

            if (configRes && configRes.success && configRes.data) {
                setConfig(configRes.data);
                setIsScreenCoverVisible(
                    configRes.data.isScreenCoverVisible !== undefined
                        ? Boolean(configRes.data.isScreenCoverVisible)
                        : true
                );
            }

            const now = new Date();
            const dateStr = now.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: '2-digit' });
            const timeStr = now.toLocaleTimeString('es-MX', { hour12: false });
            setLastUpdated(`${dateStr} a las ${timeStr} hrs.`);
        } catch (error) {
            console.error("Error al sincronizar datos del panel de Optimo Ópticas:", error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al sincronizar datos del panel administrativo.'
            });
        } finally {
            setIsLoading(false);
        }
    }, [showMessage]);

    // Montaje y sincronización inicial
    useEffect(() => {
        loadInitialData();
    }, [loadInitialData]);

    // WebSocket / Canal en Tiempo Real para sincronización de pantalla y métricas
    useEffect(() => {
        let ws = null;
        let isMounted = true;

        const connectWS = () => {
            try {
                let wsUrl = process.env.REACT_APP_API_URL_WSS;
                if (!wsUrl && process.env.REACT_APP_API_URL) {
                    wsUrl = process.env.REACT_APP_API_URL.replace(/^http/, 'ws');
                    if (wsUrl.endsWith('/api/')) wsUrl = wsUrl.replace(/\/api\/$/, '/ws');
                    else if (wsUrl.endsWith('/api')) wsUrl = wsUrl.replace(/\/api$/, '/ws');
                    else if (!wsUrl.endsWith('/ws')) wsUrl = wsUrl.endsWith('/') ? `${wsUrl}ws` : `${wsUrl}/ws`;
                }

                if (!wsUrl && typeof window !== 'undefined') {
                    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
                    wsUrl = `${protocol}//${window.location.host}/ws`;
                }

                if (wsUrl) {
                    const finalUrl = `${wsUrl}${wsUrl.includes('?') ? '&' : '?'}tipo=superadmin`;
                    ws = new WebSocket(finalUrl);
                    webSocketRef.current = ws;

                    ws.onmessage = (event) => {
                        if (!isMounted) return;
                        try {
                            const data = JSON.parse(event.data);
                            // Sincronización en tiempo real de visibilidad de pantalla
                            if (data.type === 'OPTIMO_OPTICAS_SCREEN_VISIBILITY') {
                                setIsScreenCoverVisible(Boolean(data.visible));
                            } else if (data.type === 'OPTIMO_OPTICAS_METRIC') {
                                // Incrementar KPI en vivo reactivamente
                                if (data.eventName) {
                                    setStats(prev => {
                                        const next = { ...prev };
                                        if (data.eventName === 'qr_scan') next.qrScan += 1;
                                        else if (data.eventName === 'page_view') next.pageView += 1;
                                        else if (data.eventName === 'form_start') next.formStart += 1;
                                        else if (data.eventName === 'form_complete') next.formComplete += 1;
                                        else if (data.eventName === 'game_start') next.gameStart += 1;
                                        else if (data.eventName === 'coupon_discovered') next.couponsDiscovered += 1;
                                        else if (data.eventName === 'screenshot_saved') next.screenshotsSaved += 1;
                                        else if (data.eventName === 'coupon_recovered') next.couponsRecovered += 1;
                                        else if (data.eventName === 'coupon_redeemed') next.couponsRedeemed += 1;
                                        return next;
                                    });
                                }
                            } else if (data.type === 'OPTIMO_OPTICAS_PARTICIPANT_REGISTERED') {
                                loadInitialData();
                            }
                        } catch (e) {
                            // Mensajes no-JSON ignorados
                        }
                    };
                }
            } catch (err) {
                console.warn("Fallo no bloqueante al conectar WebSocket de administración:", err);
            }
        };

        connectWS();

        return () => {
            isMounted = false;
            if (ws && ws.readyState === WebSocket.OPEN) {
                try {
                    ws.close();
                } catch (e) {
                    // Safe cleanup
                }
            }
        };
    }, [loadInitialData]);

    const handleReturn = () => {
        if (getOutOfTheDynamic) {
            getOutOfTheDynamic();
        } else {
            window.location.href = '/admin/dashboard';
        }
    };

    /**
     * Proyecta la portada de Optimo Ópticas en pantalla gigante (/screen Tile 49).
     * Sincronizado vía WebSocket al estilo de la dinámica Rooster.
     */
    const handleProjectOnScreen = async () => {
        try {
            setIsProjecting(true);
            const res = await SendQROptimoOpticas();
            if (res && res.success !== false) {
                setIsScreenCoverVisible(true);
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: 'Portada de Optimo Ópticas proyectada en pantalla gigante'
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res?.message || 'No se pudo enviar la instrucción a pantalla gigante'
                });
            }
        } catch {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'No se pudo enviar la instrucción a pantalla'
            });
        } finally {
            setIsProjecting(false);
        }
    };

    /**
     * Habilita o pausa temporalmente la recepción de registros de aficionados.
     */
    const triggerToggleActiveDynamic = async (nextState) => {
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Actualizando estado de la dinámica...' });
        try {
            const res = await UpdateAdminConfig({ ...config, isDynamicActive: nextState });
            if (res && res.success) {
                setConfig(prev => ({ ...prev, isDynamicActive: nextState }));
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: nextState ? 'Dinámica habilitada para aficionados.' : 'Dinámica deshabilitada temporalmente.'
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res?.message || 'Error al modificar estado de la dinámica.'
                });
            }
        } catch (err) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de conexión con el servidor.' });
        }
    };

    /**
     * Conmuta la proyección de la dinámica en pantalla gigante (/screen).
     */
    const triggerToggleScreenVisibility = (nextState) => {
        setConfirmConfig({
            message: `¿Deseas ${nextState ? 'MOSTRAR' : 'OCULTAR'} la pantalla promocional (/screen) de Optimo Ópticas en las pantallas gigantes del estadio?`,
            textYes: nextState ? 'Sí, mostrar' : 'Sí, ocultar',
            textNo: 'Cancelar',
            action: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Actualizando proyección en pantallas...' });
                try {
                    const res = await SetScreenVisibility(nextState);
                    if (res && res.success) {
                        setIsScreenCoverVisible(nextState);
                        setConfig(prev => ({ ...prev, isScreenCoverVisible: nextState }));
                        showMessage({
                            type: MESSAGE_TYPE.SUCCESS,
                            text: `La pantalla ahora está ${nextState ? 'VISIBLE' : 'OCULTA'} en el estadio.`
                        });

                        // Notificar vía WebSocket si está conectado
                        if (webSocketRef.current && webSocketRef.current.readyState === WebSocket.OPEN) {
                            webSocketRef.current.send(JSON.stringify({
                                type: 'OPTIMO_OPTICAS_SCREEN_VISIBILITY',
                                visible: nextState
                            }));
                        }
                        return { success: true };
                    } else {
                        showMessage({
                            type: MESSAGE_TYPE.BAD,
                            text: res?.message || 'Error al modificar visibilidad de pantalla.'
                        });
                        return { success: false };
                    }
                } catch (err) {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de conexión con el servidor.' });
                    return { success: false };
                }
            }
        });
        setShowConfirm(true);
    };

    /**
     * Remueve la descalificación de un aficionado justificadamente.
     */
    const triggerUndisqualify = (participant) => {
        setConfirmConfig({
            message: `¿Deseas restituir los derechos de cupón a "${participant.name}" (${participant.couponCode || 'Sin folio'})?`,
            textYes: 'Sí, restituir participante',
            textNo: 'Cancelar',
            action: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Restituyendo participante...' });
                try {
                    const res = await UndisqualifyParticipant(participant.id);
                    if (res && res.success) {
                        setParticipants(prev => prev.map(p =>
                            p.id === participant.id
                                ? { ...p, isDisqualified: false, disqualifiedReason: null }
                                : p
                        ));
                        showMessage({
                            type: MESSAGE_TYPE.SUCCESS,
                            text: `El participante ${participant.name} fue restituido correctamente.`
                        });
                        return { success: true };
                    } else {
                        showMessage({
                            type: MESSAGE_TYPE.BAD,
                            text: res?.message || 'Error al restituir al participante.'
                        });
                        return { success: false };
                    }
                } catch (err) {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de conexión al restituir.' });
                    return { success: false };
                }
            }
        });
        setShowConfirm(true);
    };

    /**
     * Reinicia el estado de la dinámica para una nueva jornada o partido.
     */
    const triggerResetDynamic = () => {
        setConfirmConfig({
            message: '⚠️ ¿Estás COMPLETAMENTE SEGURO de que deseas reiniciar la dinámica? Esta acción archivará a los participantes actuales y reiniciará el stock de cupones para el nuevo partido.',
            textYes: 'Sí, reiniciar dinámica',
            textNo: 'Cancelar',
            action: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Reiniciando dinámica de Optimo Ópticas...' });
                try {
                    const res = await ResetDynamic();
                    if (res && res.success) {
                        setParticipants([]);
                        setStats({
                            qrScan: 0,
                            pageView: 0,
                            formStart: 0,
                            formComplete: 0,
                            gameStart: 0,
                            couponsDiscovered: 0,
                            screenshotsSaved: 0,
                            couponsRecovered: 0,
                            couponsRedeemed: 0
                        });
                        setLastUpdated(new Date().toLocaleTimeString('es-MX', { hour12: false }) + ' hrs');
                        showMessage({
                            type: MESSAGE_TYPE.SUCCESS,
                            text: '¡La dinámica de Optimo Ópticas ha sido reiniciada con éxito!'
                        });
                        return { success: true };
                    } else {
                        showMessage({
                            type: MESSAGE_TYPE.BAD,
                            text: res?.message || 'Error al reiniciar la dinámica.'
                        });
                        return { success: false };
                    }
                } catch (err) {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de conexión al reiniciar.' });
                    return { success: false };
                }
            }
        });
        setShowConfirm(true);
    };

    /**
     * Descarga del informe ejecutivo oficial en Excel (.xlsx) generado con ClosedXML.
     */
    const handleExportExcel = () => {
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Preparando descarga del informe ejecutivo oficial...' });
        try {
            const url = GetExportExcelUrl();
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Reporte_Optimo_Opticas_${new Date().toISOString().slice(0, 10)}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            showMessage({
                type: MESSAGE_TYPE.SUCCESS,
                text: 'Descarga de informe Excel iniciada correctamente.'
            });
        } catch (e) {
            console.error("Error al exportar Excel:", e);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al iniciar la descarga del informe ejecutivo.'
            });
        }
    };

    // ==========================================
    // FILTRADO Y PAGINACIÓN
    // ==========================================
    const filteredParticipants = participants.filter(p => {
        const query = searchTerm.toLowerCase().trim();
        const matchesSearch = !query ||
            (p.name && p.name.toLowerCase().includes(query)) ||
            (p.email && p.email.toLowerCase().includes(query)) ||
            (p.phone && p.phone.includes(query)) ||
            (p.couponCode && p.couponCode.toLowerCase().includes(query));

        let matchesStatus = true;
        if (statusFilter === 'redeemed') {
            matchesStatus = Boolean(p.hasRedeemed);
        } else if (statusFilter === 'pending') {
            matchesStatus = !p.hasRedeemed && !p.isDisqualified;
        } else if (statusFilter === 'disqualified') {
            matchesStatus = Boolean(p.isDisqualified);
        }

        return matchesSearch && matchesStatus;
    });

    const totalRegistries = filteredParticipants.length;
    const totalPages = Math.ceil(totalRegistries / pageSize) || 1;
    const verifiedPage = Math.min(currentPage, totalPages);
    const startIndex = (verifiedPage - 1) * pageSize;
    const paginatedParticipants = filteredParticipants.slice(startIndex, startIndex + pageSize);

    // Contadores de cabecera
    const totalWinnersIssued = participants.filter(p => p.isWinner && !p.isDisqualified).length;
    const totalRedeemedCount = participants.filter(p => p.hasRedeemed).length;

    return (
        <SecondaryBase actionToExecute={handleReturn}>
            {/* Modal de Confirmación Universal */}
            {showConfirm && (
                <Confirm
                    message={confirmConfig.message}
                    actionToYesButton={confirmConfig.action}
                    textYesButton={confirmConfig.textYes}
                    textNoButton={confirmConfig.textNo}
                    setShowModal={setShowConfirm}
                />
            )}

            {/* Modal de Configuración de Campaña */}
            <OptimoOpticasConfigModal
                isOpen={isConfigModalOpen}
                onClose={() => setIsConfigModalOpen(false)}
                onConfigUpdated={(newCfg) => {
                    setConfig(prev => ({ ...prev, ...newCfg }));
                    if (newCfg.isScreenCoverVisible !== undefined) {
                        setIsScreenCoverVisible(Boolean(newCfg.isScreenCoverVisible));
                    }
                    loadInitialData();
                }}
            />

            {/* Modal de Validación y Canje Staff */}
            <OptimoOpticasCheckinModal
                isOpen={isCheckinModalOpen}
                onClose={() => {
                    setIsCheckinModalOpen(false);
                    loadInitialData();
                }}
                initialRedeemed={totalRedeemedCount}
                onCheckinSuccess={() => {
                    loadInitialData();
                }}
            />

            {/* Modal de Descalificación con Motivo Justificado */}
            <OptimoOpticasDisqualifyModal
                isOpen={Boolean(disqualifyTarget)}
                participant={disqualifyTarget}
                onClose={() => setDisqualifyTarget(null)}
                onDisqualified={(id, reason) => {
                    setParticipants(prev => prev.map(p =>
                        p.id === id
                            ? { ...p, isDisqualified: true, disqualifiedReason: reason }
                            : p
                    ));
                    loadInitialData();
                }}
            />

            {/* Modal de Zona Peligrosa y Reinicio con Contraseña Maestra */}
            <OptimoOpticasDangerModal
                isOpen={isDangerModalOpen}
                onClose={() => setIsDangerModalOpen(false)}
                onResetSuccess={() => {
                    setParticipants([]);
                    loadInitialData();
                }}
            />

            {/* Panel Principal */}
            <div className="container-fluid px-4 py-3 OptimoOpticasBoard">
                {/* Cabecera Principal */}
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4 border-bottom pb-3">
                    <div>
                        <h1 className="h3 fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                            <i className="bi bi-eyeglasses text-danger"></i>
                            <span>Optimo Ópticas (Rasca y Gana)</span>
                        </h1>
                        <p className="text-secondary small mb-0 d-flex align-items-center gap-2 flex-wrap">
                            <span>{config.matchTitle} — Prefijo: <strong>#{config.couponPrefix || 'OPTI'}</strong></span>
                            <span className="badge bg-danger text-white fw-semibold">
                                {config.winnerMode === 'LIMITED'
                                    ? `🎯 Cupo Limitado: ${config.maxWinners || 50} cupones`
                                    : '🎟️ Ilimitado (Todos Ganadores)'}
                            </span>
                            <span className="badge bg-dark text-white fw-semibold">
                                Vigencia Cartera: {config.storageExpirationDays || 30} días
                            </span>
                        </p>
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <button
                            type="button"
                            className="btn btn-success fw-bold d-flex align-items-center gap-2 shadow-sm"
                            onClick={() => setIsCheckinModalOpen(true)}
                        >
                            <i className="bi bi-qr-code-scan fs-6"></i>
                            <span>Escanear pases / Check in</span>
                            <span className="badge bg-white text-success fw-bold ms-1">
                                {totalRedeemedCount} / {totalWinnersIssued || (config.winnerMode === 'LIMITED' ? config.maxWinners : '∞')}
                            </span>
                        </button>

                        <button
                            type="button"
                            className="btn btn-outline-dark fw-bold d-flex align-items-center gap-2"
                            onClick={() => setIsConfigModalOpen(true)}
                        >
                            <i className="bi bi-sliders text-danger"></i>
                            <span>Configurar Dinámica</span>
                        </button>
                    </div>
                </div>

                {/* 1. Botón "Proyectar en pantalla" - Estilo Rooster con ícono QR */}
                <div className="mb-4">
                    <button 
                        type="button" 
                        onClick={handleProjectOnScreen} 
                        disabled={isProjecting}
                        className="btn btn-outline-primary btn-lg w-100 py-3 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm"
                    >
                        {isProjecting ? (
                            <>
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                <span>Proyectando en pantalla...</span>
                            </>
                        ) : (
                            <>
                                <i className="bi bi-qr-code-scan fs-4"></i>
                                <span>Proyectar en pantalla</span>
                            </>
                        )}
                    </button>
                </div>

                {/* Métricas en Tiempo Real (Funnel) - Centrado estilo Convivencia en la zona */}
                <p className="fs-4 text-center mt-4 mb-1 fw-bold">Embudo de conversión</p>
                <div className="text-center mb-3">
                    <button 
                        type="button" 
                        className="btn btn-outline-primary mb-1 fw-semibold d-inline-flex align-items-center gap-1"
                        onClick={loadInitialData}
                        disabled={isLoading}
                    >
                        <i className={`bi bi-arrow-clockwise me-1 ${isLoading ? 'animate__animated animate__rotateIn animate__infinite' : ''}`}></i> Actualizar datos
                    </button>
                    {lastUpdated && (
                        <div className="text-muted small mt-1">
                            <i className="bi bi-clock-history me-1"></i> Actualizado el {lastUpdated}
                        </div>
                    )}
                </div>

                <div 
                    className="d-flex align-items-stretch gap-3 mb-4 pb-2 text-center"
                    style={{ 
                        overflowX: 'auto', 
                        overflowY: 'hidden', 
                        WebkitOverflowScrolling: 'touch',
                        scrollbarWidth: 'thin'
                    }}
                >
                    {/* KPI 1: Escaneos QR */}
                    <div style={{ flex: '0 0 155px', minWidth: '155px' }}>
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-qr-code text-danger fs-3 mb-1"></i>
                            <div className="text-secondary small fw-semibold">1. Escaneos QR</div>
                            <div className="fs-3 fw-bold text-dark mt-1">{stats.qrScan ?? 0}</div>
                        </div>
                    </div>

                    {/* KPI 2: Visitas */}
                    <div style={{ flex: '0 0 155px', minWidth: '155px' }}>
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-globe2 text-primary fs-3 mb-1"></i>
                            <div className="text-secondary small fw-semibold">2. Visitas Web</div>
                            <div className="fs-3 fw-bold text-dark mt-1">{stats.pageView ?? 0}</div>
                        </div>
                    </div>

                    {/* KPI 3: Inicios Registro */}
                    <div style={{ flex: '0 0 155px', minWidth: '155px' }}>
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-pencil-square text-warning fs-3 mb-1"></i>
                            <div className="text-secondary small fw-semibold">3. Inicios Form</div>
                            <div className="fs-3 fw-bold text-dark mt-1">{stats.formStart ?? 0}</div>
                        </div>
                    </div>

                    {/* KPI 4: Registros Completos */}
                    <div style={{ flex: '0 0 155px', minWidth: '155px' }}>
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-check-circle-fill text-success fs-3 mb-1"></i>
                            <div className="text-secondary small fw-semibold">4. Registros OK</div>
                            <div className="fs-3 fw-bold text-dark mt-1">{stats.formComplete ?? 0}</div>
                        </div>
                    </div>

                    {/* KPI 5: Rascados Iniciados */}
                    <div style={{ flex: '0 0 155px', minWidth: '155px' }}>
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-hand-index-thumb text-info fs-3 mb-1"></i>
                            <div className="text-secondary small fw-semibold">5. Rascados Inic.</div>
                            <div className="fs-3 fw-bold text-dark mt-1">{stats.gameStart ?? 0}</div>
                        </div>
                    </div>

                    {/* KPI 6: Cupones Descubiertos */}
                    <div style={{ flex: '0 0 155px', minWidth: '155px' }}>
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-award-fill text-warning fs-3 mb-1"></i>
                            <div className="text-secondary small fw-semibold">6. Cupones Ganados</div>
                            <div className="fs-3 fw-bold text-dark mt-1">{stats.couponsDiscovered ?? 0}</div>
                        </div>
                    </div>

                    {/* KPI 7: Guardados en Galería */}
                    <div style={{ flex: '0 0 155px', minWidth: '155px' }}>
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-download text-dark fs-3 mb-1"></i>
                            <div className="text-secondary small fw-semibold">7. Guardados Foto</div>
                            <div className="fs-3 fw-bold text-dark mt-1">{stats.screenshotsSaved ?? 0}</div>
                        </div>
                    </div>

                    {/* KPI 8: Solicitados Nuevamente */}
                    <div style={{ flex: '0 0 155px', minWidth: '155px' }}>
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-arrow-repeat text-secondary fs-3 mb-1"></i>
                            <div className="text-secondary small fw-semibold">8. Recuperados</div>
                            <div className="fs-3 fw-bold text-dark mt-1">{stats.couponsRecovered ?? 0}</div>
                        </div>
                    </div>

                    {/* KPI 9: Cupones Canjeados */}
                    <div style={{ flex: '0 0 165px', minWidth: '165px' }}>
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100 border-start border-4 border-success">
                            <i className="bi bi-check2-circle text-success fs-3 mb-1"></i>
                            <div className="text-secondary small fw-semibold">9. Canjeados</div>
                            <div className="fs-3 fw-bold text-success mt-1">{stats.couponsRedeemed ?? 0}</div>
                        </div>
                    </div>
                </div>

                {/* Barra de Herramientas y Tabla Paginada */}
                <div className="row g-4">
                    <div className="col-12">
                        <div className="card shadow-sm border-0 rounded-4 p-4 bg-white">
                            {/* Toolbar */}
                            <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-3 flex-wrap gap-2">
                                <div>
                                    <h3 className="h5 fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                                        <i className="bi bi-people-fill text-danger"></i>
                                        <span>Listado de participantes</span>
                                    </h3>
                                    <span className="small text-muted">
                                        Total registrado: {totalRegistries} aficionados
                                    </span>
                                </div>

                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <button
                                        type="button"
                                        className="btn btn-outline-success fw-bold d-flex align-items-center gap-2"
                                        onClick={handleExportExcel}
                                    >
                                        <i className="bi bi-file-earmark-excel-fill text-success fs-6"></i>
                                        <span>Descargar Reporte Excel</span>
                                    </button>
                                </div>
                            </div>

                            {/* Buscador Universal y Filtros */}
                            <div className="row g-2 mb-3 bg-light p-3 rounded-3 border">
                                <div className="col-12 col-md-7">
                                    <label className="form-label small fw-semibold text-secondary mb-1">
                                        Buscador Universal
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-white border-end-0">
                                            <i className="bi bi-search text-muted"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control border-start-0"
                                            placeholder="Buscar por nombre, folio #OPTI-..., correo o teléfono..."
                                            value={searchTerm}
                                            onChange={(e) => {
                                                setSearchTerm(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className="col-12 col-md-5">
                                    <label className="form-label small fw-semibold text-secondary mb-1">
                                        Filtrar por Estado
                                    </label>
                                    <select
                                        className="form-select"
                                        value={statusFilter}
                                        onChange={(e) => {
                                            setStatusFilter(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value="all">Todos los Registros</option>
                                        <option value="redeemed">Canjeados en Sucursal</option>
                                        <option value="pending">Pendientes de Canje</option>
                                        <option value="disqualified">Descalificados</option>
                                    </select>
                                </div>
                            </div>

                            {/* Tabla de Participantes */}
                            {isLoading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-danger" role="status"></div>
                                    <p className="text-secondary small mt-2">Cargando participantes...</p>
                                </div>
                            ) : paginatedParticipants.length > 0 ? (
                                <div>
                                    <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                        <table className="table align-middle table-hover border-secondary-subtle">
                                            <thead className="table-light sticky-top" style={{ zIndex: 10 }}>
                                                <tr className="small text-secondary fw-bold text-uppercase">
                                                    <th scope="col">Folio de Cupón</th>
                                                    <th scope="col">Participante</th>
                                                    <th scope="col">Contacto</th>
                                                    <th scope="col">Fecha Registro</th>
                                                    <th scope="col" className="text-center">Opt-in Mercadotecnia</th>
                                                    <th scope="col" className="text-center">Canje en Sucursal</th>
                                                    <th scope="col" className="text-center">Acciones</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedParticipants.map((p) => {
                                                    const cleanPhone = p.phone ? String(p.phone).replace(/\D/g, '') : '';
                                                    return (
                                                        <tr
                                                            key={p.id}
                                                            className={
                                                                p.isDisqualified
                                                                    ? 'table-secondary opacity-75'
                                                                    : p.hasRedeemed
                                                                    ? 'table-success-subtle'
                                                                    : p.isWinner
                                                                    ? 'table-light'
                                                                    : ''
                                                            }
                                                        >
                                                            {/* Folio */}
                                                            <td>
                                                                {p.couponCode ? (
                                                                    <div className="d-flex align-items-center gap-1.5 flex-wrap">
                                                                        <span className="badge bg-dark text-warning font-monospace px-2.5 py-1.5 fs-6 fw-bold">
                                                                            {p.couponCode}
                                                                        </span>
                                                                        {p.winnerRank > 0 && (
                                                                            <span className="badge bg-warning-subtle text-dark border border-warning small fw-bold">
                                                                                #{p.winnerRank}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-muted small">Sin folio asignado</span>
                                                                )}
                                                            </td>

                                                            {/* Participante */}
                                                            <td>
                                                                <div className="fw-bold text-dark d-flex align-items-center gap-1.5 flex-wrap">
                                                                    <span>{p.name}</span>
                                                                    {p.wasRestituted && (
                                                                        <span className="badge bg-info-subtle text-info border border-info rounded-pill fw-bold" style={{ fontSize: '0.65rem' }}>
                                                                            ♻️ Restituido
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                {p.isDisqualified && (
                                                                    <div className="mt-1">
                                                                        <span className="badge bg-danger text-white rounded-pill fw-bold" style={{ fontSize: '0.65rem' }}>
                                                                            ⛔ DESCALIFICADO
                                                                        </span>
                                                                        <small className="text-danger d-block mt-0.5" style={{ fontSize: '0.72rem' }}>
                                                                            Motivo: {p.disqualifiedReason || 'Sin motivo registrado'}
                                                                        </small>
                                                                    </div>
                                                                )}
                                                            </td>

                                                            {/* Contacto */}
                                                            <td>
                                                                <div className="small text-secondary mb-0.5">
                                                                    <i className="bi bi-envelope me-1"></i>{p.email}
                                                                </div>
                                                                <div className="small text-secondary">
                                                                    <a
                                                                        href={`https://wa.me/52${cleanPhone}`}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        className="text-decoration-none text-success fw-semibold"
                                                                    >
                                                                        <i className="bi bi-whatsapp me-1"></i>{p.phone}
                                                                    </a>
                                                                </div>
                                                            </td>

                                                            {/* Fecha Registro */}
                                                            <td className="small text-muted">
                                                                {formatMexicoDate(p.createdAtUtc)}
                                                            </td>

                                                            {/* Opt-in Mercadotecnia */}
                                                            <td className="text-center">
                                                                {p.acceptedMarketing ? (
                                                                    <span className="badge bg-success-subtle text-success border border-success px-2 py-1 fw-bold">
                                                                        <i className="bi bi-check-lg me-1"></i> SÍ (Aceptó)
                                                                    </span>
                                                                ) : (
                                                                    <span className="badge bg-secondary-subtle text-secondary border px-2 py-1">
                                                                        NO
                                                                    </span>
                                                                )}
                                                            </td>

                                                            {/* Canje en Sucursal */}
                                                            <td className="text-center">
                                                                {p.hasRedeemed ? (
                                                                    <span className="badge bg-success-subtle text-success border border-success fw-bold px-2 py-1.5 d-inline-block">
                                                                        <div><i className="bi bi-check2-circle me-1"></i> CANJEADO</div>
                                                                        <div className="text-muted fw-normal" style={{ fontSize: '0.68rem' }}>
                                                                            {formatMexicoDate(p.redeemedAtUtc)}
                                                                        </div>
                                                                        {p.redeemedBy && (
                                                                            <div className="text-secondary" style={{ fontSize: '0.62rem' }}>
                                                                                Op: {p.redeemedBy}
                                                                            </div>
                                                                        )}
                                                                    </span>
                                                                ) : p.isWinner ? (
                                                                    <span className="badge bg-warning-subtle text-dark border border-warning px-2.5 py-1 fw-semibold">
                                                                        Pendiente
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted small">-</span>
                                                                )}
                                                            </td>

                                                            {/* Acciones */}
                                                            <td className="text-center">
                                                                {p.isDisqualified ? (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => triggerUndisqualify(p)}
                                                                        className="btn btn-sm btn-outline-success fw-semibold d-inline-flex align-items-center gap-1"
                                                                        title="Quitar descalificación y restituir"
                                                                    >
                                                                        <i className="bi bi-arrow-counterclockwise"></i>
                                                                        <span>Restituir</span>
                                                                    </button>
                                                                ) : (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setDisqualifyTarget(p)}
                                                                        className="btn btn-sm btn-outline-danger fw-semibold d-inline-flex align-items-center gap-1"
                                                                        title="Descalificar con motivo justificado"
                                                                    >
                                                                        <i className="bi bi-shield-x"></i>
                                                                        <span>Descalificar</span>
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Paginación */}
                                    <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top flex-wrap gap-2">
                                        <div className="small text-secondary">
                                            Mostrando {startIndex + 1} - {Math.min(startIndex + pageSize, totalRegistries)} de {totalRegistries} participantes
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <select
                                                className="form-select form-select-sm border-secondary-subtle"
                                                style={{ width: '85px' }}
                                                value={pageSize}
                                                onChange={(e) => {
                                                    setPageSize(Number(e.target.value));
                                                    setCurrentPage(1);
                                                }}
                                            >
                                                <option value={10}>10 / pág</option>
                                                <option value={25}>25 / pág</option>
                                                <option value={50}>50 / pág</option>
                                                <option value={100}>100 / pág</option>
                                            </select>

                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                disabled={verifiedPage === 1}
                                                onClick={() => setCurrentPage(1)}
                                                title="Primera página"
                                            >
                                                <i className="bi bi-chevron-double-left"></i>
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                disabled={verifiedPage === 1}
                                                onClick={() => setCurrentPage(verifiedPage - 1)}
                                                title="Página anterior"
                                            >
                                                <i className="bi bi-chevron-left"></i>
                                            </button>
                                            <span className="small fw-semibold text-dark px-2">
                                                Página {verifiedPage} de {totalPages}
                                            </span>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                disabled={verifiedPage === totalPages}
                                                onClick={() => setCurrentPage(verifiedPage + 1)}
                                                title="Página siguiente"
                                            >
                                                <i className="bi bi-chevron-right"></i>
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                disabled={verifiedPage === totalPages}
                                                onClick={() => setCurrentPage(totalPages)}
                                                title="Última página"
                                            >
                                                <i className="bi bi-chevron-double-right"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-5 text-secondary">
                                    <i className="bi bi-inbox fs-1 d-block mb-2 text-muted"></i>
                                    <p className="fs-5 mb-1">No se encontraron participantes registrados.</p>
                                    <p className="small text-muted mb-0">Ajusta los filtros de búsqueda o espera nuevos registros desde la web.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Sección Plegable: Estado de la Dinámica para Aficionados (Independiente del Reseteo) */}
                <div className="card border-0 rounded-3 shadow-sm bg-white mt-5">
                    <div 
                        className="card-header bg-light border-0 py-3 d-flex justify-content-between align-items-center cursor-pointer"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setIsDynamicActiveCardOpen(!isDynamicActiveCardOpen)}
                    >
                        <h3 className="h5 fw-bold mb-0 text-dark">
                            <i className="bi bi-toggle2-on me-2 text-primary"></i>
                            Estado de la Dinámica para Aficionados
                        </h3>
                        <i className={`bi ${isDynamicActiveCardOpen ? 'bi-chevron-up' : 'bi-chevron-down'} fs-5`}></i>
                    </div>
                    {isDynamicActiveCardOpen && (
                        <div className="card-body p-4 border-top">
                            <p className="small text-muted mb-3">
                                Habilita o pausa temporalmente la recepción de nuevos registros desde la web móvil (/psl-optopticas).
                            </p>
                            <button
                                type="button"
                                onClick={() => triggerToggleActiveDynamic(!config.isDynamicActive)}
                                className={`btn w-100 py-3 fw-bold d-flex align-items-center justify-content-center gap-2 ${
                                    config.isDynamicActive 
                                        ? 'btn-outline-secondary' 
                                        : 'btn-outline-danger'
                                }`}
                            >
                                <i className={`bi ${config.isDynamicActive ? 'bi-check-circle-fill text-success' : 'bi-slash-circle-fill text-danger'}`}></i>
                                {config.isDynamicActive ? 'Dinámica activa (Recibiendo registros)' : 'Dinámica cerrada (Registro bloqueado)'}
                            </button>
                        </div>
                    )}
                </div>

                {/* Sección Plegable: Zona Peligrosa / Reiniciar Dinámica (Estilo Convivencia en la Zona) */}
                <div className="card border-0 rounded-3 shadow-sm bg-white mt-4">
                    <div 
                        className="card-header bg-light border-0 py-3 d-flex justify-content-between align-items-center cursor-pointer"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setIsDangerZoneOpen(!isDangerZoneOpen)}
                    >
                        <h3 className="h5 fw-bold mb-0 text-dark">
                            <i className="bi bi-exclamation-triangle-fill me-2 text-danger"></i>
                            Zona Peligrosa: Reiniciar Dinámica
                        </h3>
                        <i className={`bi ${isDangerZoneOpen ? 'bi-chevron-up' : 'bi-chevron-down'} fs-5`}></i>
                    </div>
                    {isDangerZoneOpen && (
                        <div className="card-body p-4 border-top">
                            <div className="bg-danger-subtle p-3 rounded border border-danger border-opacity-25">
                                <h4 className="fs-6 fw-bold text-danger mb-2">
                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                    Zona Peligrosa
                                </h4>
                                <p className="small text-danger-emphasis mb-3">
                                    Reiniciar la dinámica archivará permanentemente a todos los participantes previos, limpiará las métricas y pondrá en 0 el contador de cupones para el nuevo partido. Esta acción requiere confirmación con contraseña maestra.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setIsDangerModalOpen(true)}
                                    className="btn btn-danger w-100 py-2.5 fw-bold d-flex align-items-center justify-content-center gap-2"
                                >
                                    <i className="bi bi-trash-fill"></i>
                                    Reiniciar dinámica por completo
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </SecondaryBase>
    );
}

export default OptimoOpticasBoard;
